import { WarningTwoIcon } from '@chakra-ui/icons';
import {
  Alert,
  Flex,
  Icon,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import {
  Button,
  ButtonGroup,
  FormField,
  Input,
  SupportingText,
} from '@revelio/ui';

import sourceWhaleIcon from '../../../../assets/sourcewhale-icon.png';
import themedModalStyles from '../../../Modal/themedModal.module.css';

const modalStyles = {
  header: {
    position: 'relative',
    fontSize: '18px',
    fontWeight: '600',
    lineHeight: '23px',
    color: 'text.primary',
    marginBottom: '0',
  },
  error: {
    backgroundColor: 'var(--modal-error-bg, #FDF0F3)',
    fontSize: '10px',
    lineHeight: '11px',
    fontWeight: '400',
    color: 'var(--modal-error-text, #E76A87)',
    borderRadius: 'sm',
    mb: 2,
  },
  warning: {
    backgroundColor: 'var(--modal-warning-bg, #FFF2D0)',
    fontSize: '10px',
    lineHeight: '11px',
    fontWeight: '400',
    // Legacy/light: navy text on light yellow. Dark: amber text (the bridged
    // text.primary would flip to white and be unreadable on the yellow tint).
    color: 'var(--modal-warning-text, var(--chakra-colors-text-primary))',
    borderRadius: 'sm',
    mb: 2,
  },
  link: {
    color: '#09BBF7',
  },
} as const;

interface SourceWhaleOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (apiKey: string) => void;
  savedApiKey: string | null;
}

export const SourceWhaleOnboardingModal = ({
  isOpen,
  onClose,
  onSave,
  savedApiKey,
}: SourceWhaleOnboardingModalProps) => {
  const [apiKey, setApiKey] = useState(savedApiKey ?? '');

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setApiKey(savedApiKey ?? '');
  }, [savedApiKey, isOpen]);

  const hasSavedApiKey = Boolean(savedApiKey);

  const validateApiKey = (apiKey: string) => {
    // SourceWhale API key are 43 characters long as of Jan 2025.
    // Let's allow a reasonable range of lengths in case it changes.
    return /^[A-Za-z0-9_-]{20,100}$/.test(apiKey);
  };

  const handleSubmit = () => {
    if (!validateApiKey(apiKey)) {
      return;
    }

    onSave(apiKey);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      id="source-whale-onboarding-modal"
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="sm"
    >
      <ModalOverlay />

      <ModalContent className={themedModalStyles.content} userSelect="none">
        <ModalHeader sx={modalStyles.header}>
          <Flex gap="2" alignItems="center">
            <Image src={sourceWhaleIcon} boxSize={4} />
            Connect your SourceWhale account
          </Flex>
          <ModalCloseButton size="sm" top="50%" transform="translateY(-50%)" />
        </ModalHeader>

        <ModalBody py={0}>
          <ApiKeyAlert hasSavedApiKey={hasSavedApiKey} />

          <FormField
            label="SourceWhale API Key"
            htmlFor="sourcewhale-api-key"
            supportingText={
              <>
                <SupportingText>
                  Don't know your API key? Go to{' '}
                  <Link
                    href="https://sourcewhale.app/admin#settings"
                    sx={modalStyles.link}
                    isExternal
                  >
                    SourceWhale settings
                  </Link>{' '}
                  to generate your API Key.
                </SupportingText>
                <SupportingText>
                  If you need help setting up your SourceWhale account, reach
                  out to
                  <Link
                    href="mailto:sales@reveliolabs.com?subject=SourceWhale account setup"
                    sx={modalStyles.link}
                    isExternal
                  >
                    {' '}
                    sales
                  </Link>
                  .
                </SupportingText>
              </>
            }
          >
            <Input
              id="sourcewhale-api-key"
              data-testid="sourcewhale-api-key-input"
              size="sm"
              value={apiKey}
              placeholder="API key"
              onChange={(e) => {
                setApiKey(e.target.value);
              }}
            />
          </FormField>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup size="sm">
            <Button variant="link" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={!validateApiKey(apiKey)}
              data-testid="Save-submit"
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// This modal only appears when the user has no API key set, or when a simple request to the SourceWhale API fails.
// As such, it always displays either a warning or an error message.
const ApiKeyAlert = ({ hasSavedApiKey }: { hasSavedApiKey: boolean }) => (
  <Alert
    sx={hasSavedApiKey ? modalStyles.error : modalStyles.warning}
    status={hasSavedApiKey ? 'error' : 'warning'}
  >
    <Flex gap="2" alignItems="center">
      <Icon
        as={WarningTwoIcon}
        color={hasSavedApiKey ? '#E76A87' : '#CA9201'}
      />
      {hasSavedApiKey
        ? 'Your SourceWhale API key may be invalid. Please check your API key or generate a new one if needed.'
        : `You haven't connected your SourceWhale account yet!`}
    </Flex>
  </Alert>
);
